import { Fragment, memo, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import common from "@/i18n/definitions/common";
import Tutkinnot from "@/scenes/Koulutusmuodot/AmmatillinenKoulutus/Jarjestajat/Jarjestaja/Hakemukset/Muutospyynto/components/Tutkinnot";
import MuutospyyntoWizardKoulutukset from "@/scenes/Koulutusmuodot/AmmatillinenKoulutus/Jarjestajat/Jarjestaja/Hakemukset/Muutospyynto/components/MuutospyyntoWizardKoulutukset";
import MuutospyyntoWizardMuut from "@/scenes/Koulutusmuodot/AmmatillinenKoulutus/Jarjestajat/Jarjestaja/Hakemukset/Muutospyynto/components/MuutospyyntoWizardMuut";
import MuutospyyntoWizardOpiskelijavuodet from "@/scenes/Koulutusmuodot/AmmatillinenKoulutus/Jarjestajat/Jarjestaja/Hakemukset/Muutospyynto/components/MuutospyyntoWizardOpiskelijavuodet";
import MuutospyyntoWizardToimintaalue from "@/scenes/Koulutusmuodot/AmmatillinenKoulutus/Jarjestajat/Jarjestaja/Hakemukset/Muutospyynto/components/MuutospyyntoWizardToimintaalue";
import Section from "@/components/03-templates/Section";
import MuutospyyntoWizardTopThree from "@/scenes/Koulutusmuodot/AmmatillinenKoulutus/Jarjestajat/Jarjestaja/Hakemukset/Muutospyynto/components/MuutospyyntoWizardTopThree";
import Tutkintokielet from "@/scenes/Koulutusmuodot/AmmatillinenKoulutus/Jarjestajat/Jarjestaja/Hakemukset/Muutospyynto/components/Kielet/Tutkintokielet";
import MuutospyyntoWizardOpetuskielet from "./Jarjestajat/Jarjestaja/Hakemukset/Muutospyynto/components/MuutospyyntoWizardOpetuskielet";
import IntegroituEsikatselu from "./Jarjestajat/Jarjestaja/Hakemukset/Muutospyynto/components/Esikatselu/IntegroituEsikatselu.tsx";
import * as R from "ramda";
import isEqual from "react-fast-compare";
import { Typography } from "@mui/material";
import Lisatiedot from "@/scenes/Koulutusmuodot/AmmatillinenKoulutus/Jarjestajat/Jarjestaja/Hakemukset/Muutospyynto/components/Lisatiedot/Lisatiedot";
import Kokeilulupa from "@/scenes/Koulutusmuodot/AmmatillinenKoulutus/Jarjestajat/Jarjestaja/Hakemukset/Muutospyynto/components/Kokeilulupa.jsx";

const defaultProps = {
  isPreviewModeOn: false,
  isReadOnly: false,
  kohteet: [],
  koulutukset: {},
  koulutusalat: [],
  koulutustyypit: [],
  maaraykset: [],
  lupaKohteet: {},
  maaraystyypit: [],
  muut: [],
  opetuskielet: [],
  opiskelijavuodet: [],
  naytaMuutokset: false
};

const EsittelijatMuutospyynto = memo(
  ({
    isPreviewModeOn = defaultProps.isPreviewModeOn,
    isReadOnly = defaultProps.isReadOnly,
    kohteet: osiokohteet = defaultProps.kohteet,
    koulutukset = defaultProps.koulutukset,
    koulutusalat = defaultProps.koulutusalat,
    koulutustyypit = defaultProps.koulutustyypit,
    maaraykset = defaultProps.maaraykset,
    lupaKohteet = defaultProps.lupaKohteet,
    maaraystyypit: maaraystyypitRaw = defaultProps.maaraystyypit,
    mode,
    muut = defaultProps.muut,
    opiskelijavuodet = defaultProps.opiskelijavuodet,
    pc,
    role,
    title,
    naytaMuutokset,
    setMuutostenMaara,
    organisation
  }) => {
    const intl = useIntl();
    const [kohteet, setKohteet] = useState({});
    const [maaraystyypit, setMaaraystyypit] = useState(null);

    useEffect(() => {
      const _kohteet = R.mergeAll(
        R.flatten(
          R.map(item => {
            return {
              [R.props(["tunniste"], item)]: item
            };
          }, osiokohteet)
        )
      );
      setKohteet(_kohteet);
    }, [osiokohteet]);

    useEffect(() => {
      const _maaraystyypit = R.mergeAll(
        R.flatten(
          R.map(item => {
            return {
              [R.props(["tunniste"], item)]: item
            };
          }, maaraystyypitRaw)
        )
      );
      setMaaraystyypit(_maaraystyypit);
    }, [maaraystyypitRaw]);

    const valtakunnallinenMaarays = R.find(
      R.propEq("koodisto", "nuts1"),
      maaraykset
    );

    const sectionHeadings = useMemo(
      () => ({
        tutkinnotJaKoulutukset: {
          number: String(R.path(["1", "headingNumber"], lupaKohteet)) || "1",
          title:
            R.path(["1", "heading"], lupaKohteet) ||
            intl.formatMessage(common.lupaSectionTutkinnotMainTitle)
        },
        opetusJaTutkintokieli: {
          number: String(R.path(["2", "headingNumber"], lupaKohteet)) || "2",
          title:
            R.path(["2", "heading"], lupaKohteet) ||
            intl.formatMessage(common.lupaSectionOpetuskieliMainTitle)
        },
        toimintaalue: {
          number: String(R.path(["3", "headingNumber"], lupaKohteet)) || "3",
          title:
            R.path(["3", "heading"], lupaKohteet) ||
            intl.formatMessage(common.lupaSectionToimintaAlueMainTitle)
        },
        opiskelijavuodet: {
          number: String(R.path(["4", "headingNumber"], lupaKohteet)) || "4",
          title:
            R.path(["4", "heading"], lupaKohteet) ||
            intl.formatMessage(common.lupaSectionOpiskelijavuodetMainTitle)
        },
        muut: {
          number: String(R.path(["5", "headingNumber"], lupaKohteet)) || "5",
          title:
            R.path(["5", "heading"], lupaKohteet) ||
            intl.formatMessage(common.lupaSectionMuutMainTitle)
        }
      }),
      [intl, lupaKohteet]
    );

    return isPreviewModeOn ? (
      <IntegroituEsikatselu
        naytaMuutokset={naytaMuutokset}
        setMuutostenMaara={setMuutostenMaara}
        organisation={organisation}
      />
    ) : (
      <form className="mb-16">
        {role === "ESITTELIJA" && (
          <Fragment>
            <Section>
              <div className="w-1/3 pt-12">
                <MuutospyyntoWizardTopThree />
              </div>
            </Section>
            <Kokeilulupa />
          </Fragment>
        )}

        {title && (
          <Typography component="h2" variant="h2">
            {title}
          </Typography>
        )}

        <Tutkinnot
          code={sectionHeadings.tutkinnotJaKoulutukset.number}
          isPreviewModeOn={isPreviewModeOn}
          isReadOnly={isReadOnly}
          koulutusalat={koulutusalat}
          maaraykset={maaraykset}
          koulutustyypit={koulutustyypit}
          mode={mode}
          title={sectionHeadings.tutkinnotJaKoulutukset.title}
        />
        <MuutospyyntoWizardKoulutukset
          isReadOnly={isReadOnly}
          koulutukset={koulutukset}
          maaraykset={maaraykset}
          mode={mode}
        />
        <MuutospyyntoWizardOpetuskielet
          isReadOnly={isReadOnly}
          mode={mode}
          sectionHeadingsOpetusJaTutkintokieli={
            sectionHeadings.opetusJaTutkintokieli
          }></MuutospyyntoWizardOpetuskielet>
        <Tutkintokielet
          koulutusalat={koulutusalat}
          maaraykset={maaraykset}
          mode={mode}
        />
        <Lisatiedot
          mode={mode}
          sectionId={"lisatiedot_opetuskielet"}
          title={intl.formatMessage(common.lisatietojaOpetusJaTutkintokielista)}
          maaraykset={maaraykset}
        />
        <MuutospyyntoWizardToimintaalue
          code={String(sectionHeadings.toimintaalue.number)}
          isReadOnly={isReadOnly}
          lupakohde={lupaKohteet[3]}
          mode={mode}
          sectionId={"toimintaalue"}
          title={sectionHeadings.toimintaalue.title}
          valtakunnallinenMaarays={valtakunnallinenMaarays}
        />
        <Lisatiedot
          mode={mode}
          sectionId={"lisatiedot_toimintaalue"}
          title={intl.formatMessage(common.lisatietojaToimintaAlueesta)}
          maaraykset={maaraykset}
        />
        {kohteet.opiskelijavuodet && (
          <MuutospyyntoWizardOpiskelijavuodet
            code={String(sectionHeadings.opiskelijavuodet.number)}
            lupaKohteet={lupaKohteet}
            maaraykset={maaraykset}
            mode={mode}
            muut={muut}
            opiskelijavuodet={opiskelijavuodet}
            sectionId={"opiskelijavuodet"}
            title={sectionHeadings.opiskelijavuodet.title}
          />
        )}
        {kohteet.opiskelijavuodet && (
          <Lisatiedot
            mode={mode}
            sectionId={"lisatiedot_opiskelijavuodet"}
            title={intl.formatMessage(common.lisatietojaOpiskelijavuosista)}
            maaraykset={maaraykset}
          />
        )}
        {kohteet.muut && muut && maaraystyypit && (
          <MuutospyyntoWizardMuut
            code={sectionHeadings.muut.number}
            isReadOnly={isReadOnly}
            maaraykset={maaraykset}
            mode={mode}
            muut={muut}
            pc={pc}
            sectionId={"muut"}
            title={sectionHeadings.muut.title}
          />
        )}
      </form>
    );
  },
  (cp, np) => isEqual(cp, np)
);

EsittelijatMuutospyynto.propTypes = {
  isPreviewModeOn: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  kohteet: PropTypes.array,
  koulutukset: PropTypes.object,
  koulutusalat: PropTypes.array,
  koulutustyypit: PropTypes.array,
  maaraykset: PropTypes.array,
  mode: PropTypes.string,
  lupaKohteet: PropTypes.object,
  maaraystyypit: PropTypes.array,
  muut: PropTypes.array,
  opiskelijavuodet: PropTypes.array,
  role: PropTypes.string,
  pc: PropTypes.object,
  title: PropTypes.string,
  naytaMuutokset: PropTypes.bool,
  setMuutostenMaara: PropTypes.func,
  organisation: PropTypes.object
};

EsittelijatMuutospyynto.displayName = "EsittelijatMuutospyynto";

export default EsittelijatMuutospyynto;
